import { ref, computed, readonly } from 'vue'
import type { Document } from '@/types/generated'

/**
 * Represents a failed sync operation that can be retried
 */
export interface FailedOperation {
  type: 'send' | 'import'
  documentUuid?: string
  actaportId?: string
  document?: Document | { actaport_id: string }
  error?: string
}

/**
 * Composable for managing sync operation notifications
 * Provides a unified way to track document sync operations with Actaport
 */
export const useSyncNotifications = () => {
  // Core state - tracks operations in progress
  const pendingOperations = ref(0)
  const completedOperations = ref(0)
  const failedOperations = ref(0)
  const errors = ref<string[]>([])
  const failedOperationsList = ref<FailedOperation[]>([])

  // Auto-dismiss timer reference
  let dismissTimer: ReturnType<typeof setTimeout> | null = null

  // Computed banner display state
  const status = computed(() => {
    if (pendingOperations.value > 0) return 'syncing'
    if (failedOperations.value > 0) return 'error'
    if (completedOperations.value > 0) return 'success'
    return null
  })

  // Count to display in the banner
  const displayCount = computed(() => {
    // While syncing, show remaining count
    // When done, show total processed
    return pendingOperations.value > 0 ? pendingOperations.value : completedOperations.value
  })

  // Error message for the banner
  const errorMessage = computed(() => {
    if (failedOperations.value > 0) {
      if (errors.value.length === 1 && errors.value[0]) {
        return errors.value[0]
      }
      const total = completedOperations.value
      return `${failedOperations.value} of ${total} operations failed`
    }
    return errors.value[0] || ''
  })

  // Clear any existing auto-dismiss timer
  const clearDismissTimer = () => {
    if (dismissTimer) {
      clearTimeout(dismissTimer)
      dismissTimer = null
    }
  }

  // Start tracking new operations
  const startOperations = (count: number) => {
    clearDismissTimer()
    pendingOperations.value += count
  }

  // Mark an operation as complete (success or failure)
  const completeOperation = (success: boolean, error?: string, failedOp?: FailedOperation) => {
    if (pendingOperations.value > 0) {
      pendingOperations.value--
      completedOperations.value++

      if (!success) {
        failedOperations.value++
        if (error) {
          // Limit error messages to prevent memory issues
          if (errors.value.length < 10) {
            errors.value.push(error)
          }
        }
        // Track the failed operation for retry
        if (failedOp) {
          failedOperationsList.value.push({
            ...failedOp,
            error
          })
        }
      }
    }

    // Auto-dismiss success banner after 5 seconds when all operations complete
    if (pendingOperations.value === 0 && failedOperations.value === 0) {
      clearDismissTimer()
      dismissTimer = setTimeout(reset, 5000)
    }
  }

  // Reset all state
  const reset = () => {
    clearDismissTimer()
    pendingOperations.value = 0
    completedOperations.value = 0
    failedOperations.value = 0
    errors.value = []
    failedOperationsList.value = []
  }

  // Get detailed error information
  const getErrors = () => {
    return [...errors.value]
  }

  // Get failed operations for retry
  const getFailedOperations = () => {
    return [...failedOperationsList.value]
  }

  // Check if there are any failed operations that can be retried
  const hasFailedOperations = computed(() => {
    return failedOperationsList.value.length > 0
  })

  // Check if operations are in progress
  const isActive = computed(() => {
    return pendingOperations.value > 0 || completedOperations.value > 0
  })

  return {
    // State for banner component
    status: readonly(status),
    count: readonly(displayCount),
    errorMessage: readonly(errorMessage),
    isActive: readonly(isActive),
    hasFailedOperations: readonly(hasFailedOperations),

    // Methods for the view to call
    startOperations,
    completeOperation,
    reset,
    getErrors,
    getFailedOperations
  }
}
